import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { generatePath } from 'store/navigation/navigation';
import { routes } from 'views/navigation/routes/routes';
import {
  Row,
  Column,
  Heading,
  Spacer,
  Text,
  ButtonGroup,
  Flex,
  Popover,
  Button,
  Message,
} from '@oliasoft-open-source/react-ui-library';
import {
  addTasks,
  getTimeTrackerItems,
  listCleared,
  syncTimeCostTracker,
  updateStartTime,
  updateTrackerStartTime,
} from 'store/entities/time-tracker/time-tracker';
import { selectIsTimeTrackerLatest } from 'src/store/entities/time-tracker/selectors';
import translations from 'src/internationalisation/translation-map.json';
import { DateTimePicker } from 'src/views/time-tracker/date-time-picker/date-time-picker';
import { round } from '@oliasoft-open-source/units';
import { ConfirmModal } from 'views/modals/confirm-modal';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { formatTimeToUI } from './utils/date/dateUtils';

const TimeTrackerHeader = ({
  well,
  firstOperation,
  lastOperation,
  totalAFE,
  totalVariance,
  isPageDisabled,
  onViewModeChange,
  viewMode,
  syncTimeCostTracker,
  updateStartTime,
  updateTrackerStartTime,
  isLatestVersion,
  isInActualCampaign,
  campaignName,
  campaignId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { project: projectId, company } = useParams();
  const [showSyncModal, setShowSyncModal] = useState(false);
  const onSyncModal = () => {
    syncTimeCostTracker(projectId);
  };
  const isEditable = !isPageDisabled && !isInActualCampaign;

  const updateStart = (newStart) => {
    updateTrackerStartTime(projectId, newStart);
  };

  const onViewCampaign = () => {
    const path = generatePath(routes.campaigns, { company, project: projectId }, true);
    navigate(path, { state: { campaignId } });
  };

  const confirmSyncMessage = (
    <div>
      <p>{t(translations.timeTracker_syncConfirmationQuestion)}</p>
      <p>{t(translations.timeTracker_syncConfirmationWarning)}</p>
    </div>
  );

  return (
    <>
      <Flex gap justifyContent="space-between">
        <Flex gap="12px">
          <Heading top testId="time-tracker-title">
            {t(translations.timeTracker_timeTracker)}
          </Heading>
          <ButtonGroup
            testId="result-view-button-group"
            items={[
              { key: 1, icon: 'table', testId: 'table' },
              { key: 0, icon: 'chart', testId: 'chart' },
            ]}
            small
            onSelected={(key) => onViewModeChange(key)}
            value={viewMode ? 1 : 0}
          />
          {!isLatestVersion && (
            <div>
              <Button
                small
                label={t(translations.timeTracker_syncTimeTrackerToLatestSimulation)}
                icon="sync"
                onClick={() => setShowSyncModal(true)}
              />
            </div>
          )}
        </Flex>
      </Flex>
      <Spacer />
      <Flex justifyContent="space-between" alignItems="center">
        <Row alignItems="center">
          <Column flex={false}>
            <Row spacing={0}>
              <Text bold>{t(translations.timeTracker_wellName)}</Text>
            </Row>
            <Row spacing={0}>
              <Text>{well?.name}</Text>
            </Row>
          </Column>
          <Column flex={false}>
            <Row spacing={0}>
              <Text bold>{t(translations.timeTracker_totalAFE)}</Text>
            </Row>
            <Row spacing={0}>
              <Text>{`${totalAFE} days`}</Text>
            </Row>
          </Column>
          <Column flex={false}>
            <Row spacing={0}>
              <Text bold>{t(translations.timeTracker_startDateAndTime)}</Text>
            </Row>
            <Row spacing={0}>
              <Text>{formatTimeToUI(firstOperation?.start)}</Text>
              {isEditable && (
                <>
                  <Spacer width="var(--padding-xxs)" />
                  <Popover
                    content={
                      <DateTimePicker
                        updateTrackerStartTime={updateStart}
                        updateStartTime={updateStartTime}
                        firstOperation={firstOperation}
                        disabled={isPageDisabled}
                      />
                    }
                    placement="top-center"
                    disabled={isPageDisabled}
                  >
                    <Text link>
                      <div data-testid="time-tracker-header-edit-button-link">{t(translations.edit)}</div>
                    </Text>
                  </Popover>
                </>
              )}
            </Row>
          </Column>
          <Column flex={false}>
            <Row spacing={0}>
              <Text bold>{t(translations.timeTracker_estimatedFinish)}</Text>
            </Row>
            <Row spacing={0}>
              <Text>{formatTimeToUI(lastOperation?.finish)}</Text>
            </Row>
          </Column>
          <Column flex={false}>
            <Row spacing={0}>
              <div data-testid="time-tracker-header-variance-label">
                <Text bold>{t(translations.timeTracker_variance)}</Text>
              </div>
            </Row>
            <Row spacing={0}>
              <div data-testid="time-tracker-header-variance-text">
                <Text>{`${totalVariance} hours`}</Text>
              </div>
            </Row>
          </Column>
        </Row>
        {isInActualCampaign && (
          <Message
            message={{
              width: 680,
              content: (
                <Flex justifyContent="space-between" gap="var(--padding)" alignItems="center">
                  <div style={{ flex: 1 }} data-testid="time-tracker-campaign-lock-message">
                    <Text>
                      <Trans
                        i18nKey={translations.timeTracker_campaignLockMessage}
                        values={{ campaignName }}
                        components={{ bold: <Text bold /> }}
                      />
                    </Text>
                  </div>
                  <span style={{ flexShrink: 0 }}>
                    <Button
                      small
                      label={t(translations.timeTracker_viewCampaign)}
                      onClick={onViewCampaign}
                      testId="time-tracker-view-campaign-button"
                    />
                  </span>
                </Flex>
              ),
              type: 'Info',
              visible: true,
            }}
          />
        )}
      </Flex>
      <ConfirmModal
        visible={showSyncModal}
        setModalVisible={setShowSyncModal}
        title={t(translations.timeTracker_syncConfirmationTitle)}
        onConfirm={onSyncModal}
        confimLable={'sync'}
        confirmMessage={confirmSyncMessage}
      />
    </>
  );
};

const mapStateToProps = ({ entities }) => {
  // TODO: add selectors
  const { list, isFetching, disabledFields } = entities.timeTracker;
  const { estimate } = entities.projects;
  const isLatestVersion = selectIsTimeTrackerLatest(entities);
  const { well } = entities.projects.overview;
  const firstOperation = list[0];
  const lastOperation = list?.slice(-1)[0];
  const totalAFE = round(list.reduce((acc, operation) => acc + operation.afe, 0) / 24, 2);
  const totalVariance = round(
    list?.reduce(
      (sum, operation) =>
        sum +
        operation.activities.reduce((acc, activity) => acc + (activity.actual ? activity.actual - activity.afe : 0), 0),
      0,
    ),
    2,
  );
  return {
    itemsList: list,
    isFetching,
    well,
    firstOperation,
    lastOperation,
    totalAFE,
    totalVariance,
    disabledFields,
    isLatestVersion,
    isInActualCampaign: Boolean(estimate?.isInActualCampaign),
    campaignName: estimate?.campaignName ?? '',
    campaignId: estimate?.campaignId ?? null,
  };
};

const mapDispatchToProps = {
  getTimeTrackerItems,
  listCleared,
  addTasks,
  syncTimeCostTracker,
  updateStartTime,
  updateTrackerStartTime,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(TimeTrackerHeader));

export { Container as TimeTrackerHeader };
